import apiV2 from "api-v2";
import { showToast } from "utils/helpers";
import type {
	ReviewClarification,
	TriageClarificationPayload,
	BatchTriagePayload,
	DuplicateClarificationPayload,
	ApplyToAllPayload,
	EditClarificationTextPayload,
	StepCompletePayload,
	ClarificationReviewSummary,
	AIReviewResponse,
} from "../types/clarification-review.type";

interface ApiResponse<T> {
	data: T;
	message?: string;
	status: boolean;
}

export const getReviewClarifications = async (
	rfxId: number
): Promise<ReviewClarification[]> => {
	try {
		const response = await apiV2.get<ApiResponse<ReviewClarification[]>>(
			`rfx/${rfxId}/evaluation/clarifications/review`
		);
		return response.data?.status ? response.data.data : [];
	} catch (error) {
		console.error("Failed to fetch review clarifications:", error);
		return [];
	}
};

export const getReviewSummary = async (
	rfxId: number
): Promise<ClarificationReviewSummary> => {
	try {
		const response = await apiV2.get<ApiResponse<ClarificationReviewSummary>>(
			`rfx/${rfxId}/evaluation/clarifications/review`
		);
		const clarifications = response.data?.status ? response.data.data : [];
		const items = clarifications as unknown as ReviewClarification[];
		return {
			total: items.length,
			reviewed: items.filter((c) => c.triage_status !== null).length,
			accepted: items.filter((c) => c.triage_status === "accepted").length,
			declined: items.filter((c) => c.triage_status === "declined").length,
			duplicate: items.filter((c) => c.triage_status === "duplicate").length,
			pending: items.filter((c) => c.triage_status === null).length,
		};
	} catch (error) {
		console.error("Failed to compute review summary:", error);
		return { total: 0, reviewed: 0, accepted: 0, declined: 0, duplicate: 0, pending: 0 };
	}
};

export const triageClarification = async (
	rfxId: number,
	clarificationId: number,
	payload: TriageClarificationPayload
): Promise<ReviewClarification | null> => {
	try {
		const response = await apiV2.post<ApiResponse<ReviewClarification>>(
			`rfx/${rfxId}/evaluation/clarifications/${clarificationId}/triage`,
			payload
		);
		if (response.data?.status) {
			return response.data.data;
		}
		showToast(
			response.data?.message || "Failed to triage clarification",
			"Error",
			false
		);
		return null;
	} catch (error) {
		console.error("Failed to triage clarification:", error);
		showToast("Failed to triage clarification", "Error", false);
		return null;
	}
};

export const batchTriageClarifications = async (
	rfxId: number,
	payload: BatchTriagePayload
): Promise<ReviewClarification[] | null> => {
	try {
		const response = await apiV2.post<ApiResponse<ReviewClarification[]>>(
			`rfx/${rfxId}/evaluation/clarifications/batch-triage`,
			payload
		);
		if (response.data?.status) {
			return response.data.data;
		}
		showToast(
			response.data?.message || "Failed to batch triage clarifications",
			"Error",
			false
		);
		return null;
	} catch (error) {
		console.error("Failed to batch triage:", error);
		showToast("Failed to batch triage clarifications", "Error", false);
		return null;
	}
};

export const linkDuplicates = async (
	rfxId: number,
	clarificationId: number,
	payload: DuplicateClarificationPayload
): Promise<ReviewClarification[] | null> => {
	try {
		const response = await apiV2.post<ApiResponse<ReviewClarification[]>>(
			`rfx/${rfxId}/evaluation/clarifications/${clarificationId}/duplicate`,
			payload
		);
		if (response.data?.status) {
			return response.data.data;
		}
		showToast(
			response.data?.message || "Failed to link duplicates",
			"Error",
			false
		);
		return null;
	} catch (error) {
		console.error("Failed to link duplicates:", error);
		showToast("Failed to link duplicates", "Error", false);
		return null;
	}
};

export const applyToAll = async (
	rfxId: number,
	clarificationId: number,
	_payload: ApplyToAllPayload
): Promise<ReviewClarification[] | null> => {
	try {
		const response = await apiV2.post<ApiResponse<ReviewClarification[]>>(
			`rfx/${rfxId}/evaluation/clarifications/${clarificationId}/apply-to-all`
		);
		if (response.data?.status) {
			showToast("Clarification applied to all suppliers", "Success", true);
			return response.data.data;
		}
		showToast(
			response.data?.message || "Failed to apply to all",
			"Error",
			false
		);
		return null;
	} catch (error) {
		console.error("Failed to apply to all:", error);
		showToast("Failed to apply clarification to all suppliers", "Error", false);
		return null;
	}
};

export const editClarificationText = async (
	rfxId: number,
	clarificationId: number,
	payload: EditClarificationTextPayload
): Promise<ReviewClarification | null> => {
	try {
		const response = await apiV2.patch<ApiResponse<ReviewClarification>>(
			`rfx/${rfxId}/evaluation/clarifications/${clarificationId}/text`,
			payload
		);
		if (response.data?.status) {
			return response.data.data;
		}
		showToast(
			response.data?.message || "Failed to edit clarification",
			"Error",
			false
		);
		return null;
	} catch (error) {
		console.error("Failed to edit clarification text:", error);
		showToast("Failed to edit clarification text", "Error", false);
		return null;
	}
};

export const requestAIReview = async (
	rfxId: number
): Promise<AIReviewResponse | null> => {
	try {
		const response = await apiV2.post<ApiResponse<AIReviewResponse>>(
			`rfx/${rfxId}/evaluation/clarifications/ai-review`
		);
		if (response.data?.status) {
			return response.data.data;
		}
		showToast(
			response.data?.message || "AI review unavailable",
			"Error",
			false
		);
		return null;
	} catch (error) {
		console.error("Failed to request AI review:", error);
		showToast("AI review is currently unavailable. You can continue reviewing manually.", "Error", false);
		return null;
	}
};

export const completeReviewStep = async (
	rfxId: number,
	payload: StepCompletePayload
): Promise<boolean> => {
	try {
		const response = await apiV2.post<ApiResponse<boolean>>(
			`rfx/${rfxId}/evaluation/clarifications/step-complete`,
			payload
		);
		if (response.data?.status) {
			showToast("Clarification review step completed", "Success", true);
			return true;
		}
		showToast(
			response.data?.message || "Failed to complete review step",
			"Error",
			false
		);
		return false;
	} catch (error) {
		console.error("Failed to complete review step:", error);
		showToast("Failed to complete clarification review step", "Error", false);
		return false;
	}
};
